import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  type JSX,
} from "solid-js";
import { ApiError, LatestRequest } from "../api";
import { formatInteger } from "../format";
import { useAppState } from "../state";
import type {
  ApiSuccess,
  ChildPath,
  EaclObject,
  ObjectPage,
  RelationshipPage,
  ResourceCount,
} from "../types";
import {
  DisclosureButton,
  DisclosureIcon,
  EmptyState,
  ErrorBlock,
  InlineError,
  InlineLoading,
  LoadingBlock,
  MetaTiming,
  Pagination,
  TypeBadge,
  identifierLabel,
} from "./Common";

const resourceKey = (resource: EaclObject) => `${resource.type}:${resource.id}`;
type ScopedSuccess<T> = {
  scope: string;
  envelope: ApiSuccess<T>;
};

const scopeKey = (...parts: unknown[]) => JSON.stringify(parts);

const initialCountLimit = 1_000;
const maximumCountLimit = 30_000;

const nextCountLimit = (current: number) =>
  Math.min(maximumCountLimit, current * 2);

export function resourceCountPresentation(
  count: ResourceCount | undefined,
  observedRangeEnd: number,
  pageHasNext: boolean,
): { value: number; truncated: boolean } {
  const counted = count?.count ?? 0;
  const value = Math.max(counted, observedRangeEnd);
  return {
    value,
    truncated:
      Boolean(count?.truncated) || value > counted || (!count && pageHasNext),
  };
}

function RelationshipGroup(props: {
  parent: EaclObject;
  path: ChildPath;
  ancestry: ReadonlySet<string>;
  traversal?: string;
}): JSX.Element {
  const app = useAppState();
  const key = () =>
    `relationship:${props.traversal ?? ""}:${resourceKey(props.parent)}:${props.path.resourceType}:${props.path.relation}`;
  const expanded = createMemo(() => app.isExpanded(key()));
  const request = new LatestRequest();
  const [cursors, setCursors] = createSignal<string[]>([]);
  const cursor = () => cursors().at(-1);
  const relationshipScope = () =>
    scopeKey(props.parent.type, props.parent.id, props.path.resourceType,
      props.path.relation, app.activeQueryBasis(), JSON.stringify(app.consistency()));
  const source = () => expanded() ? ({
    subject: { type: props.parent.type, id: props.parent.id },
    resourceType: props.path.resourceType,
    relation: props.path.relation,
    pageSize: app.pageSize(),
    after: cursor(),
    cache: app.cacheEnabled(),
    populateCache: app.populateCache(),
    consistency: app.consistency(),
    scope: relationshipScope(),
    basisGeneration: app.basisGeneration(),
    queryGeneration: app.queryGeneration(),
  }) : false;
  const [relationships, { refetch }] = createResource(source, async (input) => ({
    scope: input.scope,
    envelope: await app.runQuery<RelationshipPage>(request, "/list-relationships", {
      method: "POST",
      body: JSON.stringify({
        subject: input.subject,
        resourceType: input.resourceType,
        relation: input.relation,
        pageSize: input.pageSize,
        after: input.after,
        cache: input.cache,
        populateCache: input.populateCache,
        consistency: input.consistency,
      }),
    }),
  }) satisfies ScopedSuccess<RelationshipPage>);
  const [displayedRelationships, setDisplayedRelationships] =
    createSignal<ApiSuccess<RelationshipPage>>();
  const [displayedRelationshipScope, setDisplayedRelationshipScope] =
    createSignal("");
  const [displayedCursors, setDisplayedCursors] = createSignal<string[]>([]);
  const [pendingAction, setPendingAction] = createSignal<
    "first" | "previous" | "next"
  >();

  createEffect(() => {
    if (relationships.loading || relationships.error) return;
    const result = relationships();
    if (!result) return;
    setDisplayedRelationships(result.envelope);
    setDisplayedRelationshipScope(result.scope);
    setDisplayedCursors([...cursors()]);
  });
  createEffect(() => {
    if (!relationships.loading) setPendingAction(undefined);
  });

  createEffect(
    on(
      () =>
        [
          app.pageSize(),
          app.basisGeneration(),
          JSON.stringify(app.consistency()),
        ] as const,
      () => setCursors((current) => (current.length ? [] : current)),
      { defer: true },
    ),
  );
  onCleanup(() => request.abort());
  const settledRelationships = () =>
    displayedRelationshipScope() === relationshipScope()
      ? displayedRelationships()
      : undefined;
  const navigationAction = () => {
    if (cursors().length > displayedCursors().length) return "next" as const;
    if (!cursors().length && displayedCursors().length) return "first" as const;
    if (cursors().length < displayedCursors().length)
      return "previous" as const;
    return undefined;
  };
  const navigate = (
    action: "first" | "previous" | "next",
    nextCursors: string[],
  ) => {
    if (relationships.loading) return;
    setPendingAction(action);
    setCursors(nextCursors);
  };
  const retryRelationships = () => {
    setPendingAction(navigationAction());
    void refetch();
  };
  const relationshipRecovery = () => {
    if (!cursors().length) return undefined;
    return relationships.error instanceof ApiError &&
      relationships.error.code === "invalid-cursor"
      ? { label: "First page", action: () => navigate("first", []) }
      : {
          label: "Previous page",
          action: () => navigate("previous", displayedCursors().slice(0, -1)),
        };
  };

  return (
    <div class="relationship-group">
      <div class="relationship-group__header" onClick={(event) => {
        if (!(event.target as Element).closest("button, a, input, select")) app.toggleExpanded(key());
      }}>
        <DisclosureButton
          expanded={expanded()}
          controls={`${key()}-content`}
          onClick={() => app.toggleExpanded(key())}
        >
          <TypeBadge type={props.path.resourceType} />
          <span class="relationship-group__title">
            {identifierLabel(props.path.resourceType)}s{" "}
            <small>via :{props.path.relation}</small>
          </span>
        </DisclosureButton>
        <MetaTiming meta={settledRelationships()?.meta} />
        <Show when={expanded() && settledRelationships()}>
          {(envelope) => (
            <>
              {" "}
              <Pagination
                query="read-relationships"
                page={displayedCursors().length + 1}
                canPrevious={displayedCursors().length > 0}
                canNext={envelope().data.pageInfo.hasNextPage}
                busy={relationships.loading}
                busyAction={pendingAction()}
                first={() => navigate("first", [])}
                previous={() =>
                  navigate("previous", displayedCursors().slice(0, -1))
                }
                next={() => {
                  const next = envelope().data.pageInfo.endCursor;
                  if (next) navigate("next", [...displayedCursors(), next]);
                }}
              />
            </>
          )}
        </Show>
      </div>
      <Show when={expanded()}>
        <div id={`${key()}-content`} class="relationship-group__content">
          <Show when={relationships.loading && !settledRelationships()}>
            <LoadingBlock
              label={`relationships page ${formatInteger(cursors().length + 1)}`}
            />
          </Show>
          <Show when={relationships.error}>
            <ErrorBlock
              label={`Relationships page ${formatInteger(cursors().length + 1)} failed`}
              error={relationships.error}
              retry={retryRelationships}
              secondary={relationshipRecovery()}
            />
          </Show>
          <Show when={settledRelationships()}>
            {(envelope: () => ApiSuccess<RelationshipPage>) => (
              <>
                <div
                  class="resource-children"
                  aria-busy={relationships.loading}
                >
                  <For
                    each={envelope().data.items}
                    fallback={
                      <EmptyState>
                        No authorized resources on this page.
                      </EmptyState>
                    }
                  >
                    {(relationship) => (
                      <ResourceNode
                        resource={relationship.resource}
                        ancestry={props.ancestry}
                        traversal={key()}
                      />
                    )}
                  </For>
                </div>
              </>
            )}
          </Show>
        </div>
      </Show>
    </div>
  );
}

function ResourceNode(props: {
  resource: EaclObject;
  ancestry: ReadonlySet<string>;
  traversal?: string;
}): JSX.Element {
  const app = useAppState();
  const key = () => resourceKey(props.resource);
  const cycle = () => props.ancestry.has(key());
  const paths = createMemo(
    () =>
      app.bootstrapData()?.data.schema.childPaths[props.resource.type] ?? [],
  );
  const expansionKey = () => `resource:${props.traversal ?? ""}:${key()}`;
  const expanded = createMemo(() => app.isExpanded(expansionKey()));
  const selected = () =>
    resourceKey(app.selectedResource() ?? { type: "", id: "" }) === key();
  const nextAncestry = createMemo(
    () => new Set([...props.ancestry, key()]) as ReadonlySet<string>,
  );

  return (
    <div class={`resource-node ${cycle() ? "resource-node--cycle" : ""}`}>
      <div
        class="resource-node__row"
        onClick={(e) => {
          if (!(e.target as Element).closest("button"))
            app.setSelectedResource(props.resource);
        }}
      >
        <Show
          when={!cycle() && paths().length > 0}
          fallback={<span class="resource-node__spacer" aria-hidden="true" />}
        >
          <button
            type="button"
            class="resource-node__toggle"
            aria-label={`${expanded() ? "Collapse" : "Expand"} ${props.resource.id}`}
            aria-expanded={expanded()}
            onClick={() => app.toggleExpanded(expansionKey())}
          >
            <DisclosureIcon expanded={expanded()} />
          </button>
        </Show>
        <button
          type="button"
          class={`resource-button ${selected() ? "resource-button--active" : ""}`}
          aria-pressed={selected()}
          onClick={() => app.setSelectedResource(props.resource)}
        >
          <TypeBadge type={props.resource.type} />
          <span class="resource-caption">
            <span class="resource-caption__id">{props.resource.id}</span>
          </span>
        </button>
        <Show when={cycle()}>
          <span class="cycle-badge">cycle</span>
        </Show>
      </div>
      <Show when={expanded() && !cycle()}>
        <div class="resource-node__children">
          <For each={paths()}>
            {(path) => (
              <RelationshipGroup
                parent={props.resource}
                path={path}
                ancestry={nextAncestry()}
                traversal={expansionKey()}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

function ResourceTypeGroup(props: { resourceType: string }): JSX.Element {
  const app = useAppState();
  const groupKey = () => `resource-type:${props.resourceType}`;
  const expanded = createMemo(() => app.isExpanded(groupKey()));
  const supported = () =>
    app
      .bootstrapData()
      ?.data.schema.permissionsByType[
        props.resourceType
      ]?.includes(app.permission());
  const pageRequest = new LatestRequest();
  const countRequest = new LatestRequest();
  const [cursors, setCursors] = createSignal<string[]>([]);
  const [countLimit, setCountLimit] = createSignal(initialCountLimit);
  const [settledPageScope, setSettledPageScope] = createSignal("");
  const [displayedPageScope, setDisplayedPageScope] = createSignal("");
  const [observedRangeScope, setObservedRangeScope] = createSignal("");
  const [greatestObservedRangeEnd, setGreatestObservedRangeEnd] =
    createSignal(0);
  const cursor = () => cursors().at(-1);
  const countScope = () =>
    scopeKey(
      app.subjectId(),
      app.permission(),
      props.resourceType,
      app.activeQueryBasis(),
      JSON.stringify([app.consistency(), app.subjectType()]),
    );
  const resourceScopeFromInput = (input: readonly unknown[]) =>
    scopeKey(input[0], input[1], input[2], input[5], input[8]);
  const base = () =>
    expanded() && app.permission() && supported()
      ? ([
          app.subjectId(),
          app.permission(),
          props.resourceType,
          app.cacheEnabled(),
          app.populateCache(),
          app.activeQueryBasis(),
          app.basisGeneration(),
          app.queryGeneration(),
          JSON.stringify([app.consistency(), app.subjectType()]),
        ] as const)
      : false;
  const pageSource = () => {
    const value = base();
    return value
      ? ([...value, app.pageSize(), cursor() ?? "", cursors().length] as const)
      : false;
  };
  const countSource = () => {
    const value = base();
    return value && settledPageScope() === countScope()
      ? ([...value, countLimit()] as const)
      : false;
  };
  const [page, { refetch: refetchPage }] = createResource(
    pageSource,
    async (input) => {
      const envelope = await app.runQuery<ObjectPage>(
        pageRequest,
        "/lookup-resources",
        {
          method: "POST",
          body: JSON.stringify({
            subject: { type: app.subjectType(), id: input[0] },
            permission: input[1],
            resourceType: input[2],
            cache: input[3],
            populateCache: input[4],
            pageSize: input[9],
            after: input[10] || undefined,
            consistency: app.consistency(),
          }),
        },
      );
      return {
        scope: resourceScopeFromInput(input),
        envelope,
        observedRangeEnd:
          Number(input[11]) * Number(input[9]) + envelope.data.items.length,
      };
    },
  );
  const [count, { refetch: refetchCount }] = createResource(
    countSource,
    async (input) =>
      ({
        scope: resourceScopeFromInput(input),
        envelope: await app.runQuery<ResourceCount>(
          countRequest,
          "/count-resources",
          {
            method: "POST",
            body: JSON.stringify({
              subject: { type: app.subjectType(), id: input[0] },
              permission: input[1],
              resourceType: input[2],
              cache: input[3],
              populateCache: input[4],
              countLimit: input[9],
              consistency: app.consistency(),
            }),
          },
        ),
      }) satisfies ScopedSuccess<ResourceCount>,
  );
  createEffect(() => {
    if (!pageSource()) pageRequest.abort();
    if (!countSource()) countRequest.abort();
  });
  const [displayedPage, setDisplayedPage] =
    createSignal<ApiSuccess<ObjectPage>>();
  const [displayedCount, setDisplayedCount] =
    createSignal<ApiSuccess<ResourceCount>>();
  const [displayedCountScope, setDisplayedCountScope] = createSignal("");
  const [displayedCursors, setDisplayedCursors] = createSignal<string[]>([]);
  const [displayedPageSize, setDisplayedPageSize] = createSignal(
    app.pageSize(),
  );
  const [pendingPageAction, setPendingPageAction] = createSignal<
    "first" | "previous" | "next"
  >();

  createEffect(() => {
    if (page.loading || page.error) return;
    const result = page();
    if (!result) return;
    // Objects are identified solely by type/id. Retain unchanged row instances
    // so a refreshed root lookup does not remount independent relationship reads.
    setDisplayedPage((previous) => {
      const existing = new Map(previous?.data.items.map((item) => [resourceKey(item), item]));
      return { ...result.envelope, data: { ...result.envelope.data,
        items: result.envelope.data.items.map((item) => existing.get(resourceKey(item)) ?? item),
      } };
    });
    app.rememberResources(result.envelope.data.items);
    setDisplayedPageScope(result.scope);
    setDisplayedCursors([...cursors()]);
    setDisplayedPageSize(app.pageSize());
    setSettledPageScope(result.scope);
    if (observedRangeScope() === result.scope) {
      setGreatestObservedRangeEnd((current) =>
        Math.max(current, result.observedRangeEnd),
      );
    } else {
      setObservedRangeScope(result.scope);
      setGreatestObservedRangeEnd(result.observedRangeEnd);
    }
  });
  createEffect(() => {
    if (!page.loading) setPendingPageAction(undefined);
  });
  createEffect(() => {
    if (count.loading || count.error) return;
    const result = count();
    if (result) {
      setDisplayedCount(result.envelope);
      setDisplayedCountScope(result.scope);
    }
  });

  createEffect(
    on(
      () =>
        [
          app.subjectId(),
          app.permission(),
          app.pageSize(),
          app.basisGeneration(),
          JSON.stringify([app.consistency(), app.subjectType()]),
        ] as const,
      () => setCursors((current) => (current.length ? [] : current)),
      { defer: true },
    ),
  );
  createEffect(
    on(
      () => countScope(),
      () => setCountLimit(initialCountLimit),
      { defer: true },
    ),
  );
  onCleanup(() => {
    pageRequest.abort();
    countRequest.abort();
  });

  const settledPage = () =>
    displayedPageScope() === countScope() ? displayedPage() : undefined;
  const settledCount = () =>
    displayedCountScope() === countScope() ? displayedCount() : undefined;
  const pageNavigationAction = () => {
    if (cursors().length > displayedCursors().length) return "next" as const;
    if (!cursors().length && displayedCursors().length) return "first" as const;
    if (cursors().length < displayedCursors().length)
      return "previous" as const;
    return undefined;
  };
  const navigatePage = (
    action: "first" | "previous" | "next",
    nextCursors: string[],
  ) => {
    if (page.loading) return;
    setPendingPageAction(action);
    setCursors(nextCursors);
  };
  const retryPage = () => {
    setPendingPageAction(pageNavigationAction());
    void refetchPage();
  };
  const pageRecovery = () => {
    if (!cursors().length) return undefined;
    return page.error instanceof ApiError &&
      page.error.code === "invalid-cursor"
      ? { label: "First page", action: () => navigatePage("first", []) }
      : {
          label: "Previous page",
          action: () =>
            navigatePage("previous", displayedCursors().slice(0, -1)),
        };
  };
  const itemCount = () => settledPage()?.data.items.length ?? 0;
  const rangeStart = () =>
    itemCount() ? displayedCursors().length * displayedPageSize() + 1 : 0;
  const rangeEnd = () =>
    displayedCursors().length * displayedPageSize() + itemCount();
  const observedRangeEnd = () =>
    observedRangeScope() === countScope()
      ? greatestObservedRangeEnd()
      : rangeEnd();
  const countPresentation = () =>
    resourceCountPresentation(
      settledCount()?.data,
      observedRangeEnd(),
      Boolean(settledPage()?.data.pageInfo.hasNextPage),
    );
  const canIncreaseCount = () =>
    Boolean(settledCount()?.data.truncated) && countLimit() < maximumCountLimit;
  const increaseCount = () => {
    if (count.loading || !canIncreaseCount()) return;
    setCountLimit(nextCountLimit(countLimit()));
  };

  return (
    <div class="group-card">
      <div class="group-card__header" onClick={(event) => {
        if (!(event.target as Element).closest("button, a, input, select")) app.toggleExpanded(groupKey());
      }}>
        <DisclosureButton
          expanded={expanded()}
          controls={`${groupKey()}-content`}
          onClick={() => app.toggleExpanded(groupKey())}
        >
          <TypeBadge type={props.resourceType} />
          <span class="group-card__title">
            {identifierLabel(props.resourceType)}s
          </span>
        </DisclosureButton>
        <Show when={expanded() || settledPage() || settledCount()}>
          <div class="group-card__stats">
            <Show when={!supported()}>
              <span>Permission not defined</span>
            </Show>
            <span class="group-card__page-stats">
              <Show
                when={settledPage()}
                fallback={
                  page.loading ? (
                    <InlineLoading
                      label={`Loading page ${formatInteger(cursors().length + 1)}`}
                    />
                  ) : page.error ? (
                    <InlineError
                      label={`Page ${formatInteger(cursors().length + 1)} failed`}
                    />
                  ) : (
                    <span class="section-meta">—</span>
                  )
                }
              >
                <span class="group-card__range">
                  {formatInteger(rangeStart())}–{formatInteger(rangeEnd())}
                </span>
                <MetaTiming meta={settledPage()?.meta} />
                <Show when={page.loading && !pendingPageAction()}>
                  <InlineLoading
                    label={`Refreshing ${props.resourceType} resources`}
                  />
                </Show>
                <Show when={page.error}>
                  <InlineError
                    label={`Page ${formatInteger(cursors().length + 1)} failed`}
                  />
                </Show>
              </Show>
            </span>
            <span class="group-card__stats-separator">of</span>
            <span class="group-card__count-stats">
              <Show
                when={settledCount()}
                fallback={
                  count.loading ? (
                    <InlineLoading
                      label={`Counting ${props.resourceType} resources`}
                    />
                  ) : count.error ? (
                    <InlineError label="Count failed" />
                  ) : (
                    <span class="section-meta">—</span>
                  )
                }
              >
                {(envelope: () => ApiSuccess<ResourceCount>) => (
                  <>
                    <Show
                      when={canIncreaseCount()}
                      fallback={
                        <span
                          class="group-card__count"
                          aria-label={
                            countPresentation().truncated
                              ? `At least ${formatInteger(countPresentation().value)} ${props.resourceType} resources`
                              : undefined
                          }
                        >
                          {formatInteger(countPresentation().value)}
                          {countPresentation().truncated ? "+" : ""}
                        </span>
                      }
                    >
                      <button
                        type="button"
                        class="group-card__count group-card__count-action"
                        aria-label={`At least ${formatInteger(countPresentation().value)} ${props.resourceType} resources. Count up to ${formatInteger(nextCountLimit(countLimit()))}`}
                        aria-busy={count.loading}
                        disabled={count.loading}
                        onClick={increaseCount}
                      >
                        {formatInteger(countPresentation().value)}+
                      </button>
                    </Show>
                    <MetaTiming meta={envelope().meta} />
                    <Show when={count.loading}>
                      <InlineLoading
                        label={`Counting ${props.resourceType} resources`}
                      />
                    </Show>
                    <Show when={count.error}>
                      <InlineError label="Count failed" />
                    </Show>
                  </>
                )}
              </Show>
            </span>
          </div>
        </Show>
        <Show when={expanded() && settledPage()}>
          {(envelope) => (
            <>
              {" "}
              <Pagination
                query="lookup-resources"
                page={displayedCursors().length + 1}
                canPrevious={displayedCursors().length > 0}
                canNext={envelope().data.pageInfo.hasNextPage}
                busy={page.loading}
                busyAction={pendingPageAction()}
                first={() => navigatePage("first", [])}
                previous={() =>
                  navigatePage("previous", displayedCursors().slice(0, -1))
                }
                next={() => {
                  const next = envelope().data.pageInfo.endCursor;
                  if (next) navigatePage("next", [...displayedCursors(), next]);
                }}
              />
            </>
          )}
        </Show>
      </div>

      <Show when={expanded()}>
        <div id={`${groupKey()}-content`} class="group-card__content">
          <Show when={page.loading && !settledPage()}>
            <LoadingBlock
              label={`${props.resourceType} page ${formatInteger(cursors().length + 1)}`}
            />
          </Show>
          <Show when={page.error}>
            <ErrorBlock
              label={`${identifierLabel(props.resourceType)} page ${formatInteger(cursors().length + 1)} failed`}
              error={page.error}
              retry={retryPage}
              secondary={pageRecovery()}
            />
          </Show>
          <Show when={count.error}>
            <ErrorBlock
              label={`${identifierLabel(props.resourceType)} count failed`}
              error={count.error}
              retry={() => void refetchCount()}
            />
          </Show>
          <Show when={supported() && displayedPage()}>
            {(envelope: () => ApiSuccess<ObjectPage>) => (
              <>
                <div class="resource-tree" aria-busy={page.loading}
                  style={{ visibility: settledPage() ? undefined : "hidden" }}>
                  <For
                    each={envelope().data.items}
                    fallback={
                      <EmptyState>No resources on this page.</EmptyState>
                    }
                  >
                    {(resource) => (
                      <ResourceNode
                        resource={resource}
                        ancestry={new Set()}
                        traversal={groupKey()}
                      />
                    )}
                  </For>
                </div>
              </>
            )}
          </Show>
        </div>
      </Show>
    </div>
  );
}

export function ResourceTreePanel(): JSX.Element {
  const app = useAppState();
  const permissions = createMemo(() => {
    const byType = app.bootstrapData()?.data.schema.permissionsByType ?? {};
    return [...new Set(Object.values(byType).flat())].sort();
  });
  return (
    <div
      class="panel-card resources-panel"
      onKeyDown={(event) => {
        if (
          ![
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
            "Home",
            "End",
          ].includes(event.key) ||
          (event.target as Element).closest("input,select")
        )
          return;
        const rows = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            ".group-card__header,.resource-node__row,.relationship-group__header",
          ),
        ).filter((row) => row.getClientRects().length);
        const row = (event.target as Element).closest<HTMLElement>(
          ".group-card__header,.resource-node__row,.relationship-group__header",
        );
        if (!row) return;
        const index = rows.indexOf(row);
        let next: HTMLElement | undefined;
        const toggle = row.querySelector<HTMLButtonElement>(
          "button[aria-expanded]",
        );
        if (event.key === "ArrowRight") {
          if (toggle?.getAttribute("aria-expanded") === "false") toggle.click();
          else next = rows[index + 1];
        }
        if (event.key === "ArrowLeft") {
          if (toggle?.getAttribute("aria-expanded") === "true") toggle.click();
          else
            next =
              row.parentElement?.parentElement
                ?.closest(".resource-node,.group-card")
                ?.querySelector<HTMLElement>(
                  ".resource-node__row,.group-card__header",
                ) ?? undefined;
        }
        if (event.key === "ArrowUp") next = rows[Math.max(0, index - 1)];
        if (event.key === "ArrowDown")
          next = rows[Math.min(rows.length - 1, index + 1)];
        if (event.key === "Home") next = rows[0];
        if (event.key === "End") next = rows.at(-1);
        event.preventDefault();
        next
          ?.querySelector<HTMLButtonElement>(".resource-button,button")
          ?.focus();
      }}
    >
      <div class="resource-toolbar">
        <fieldset aria-label="Resource Permission">
          <For each={permissions()}>
            {(permission) => (
              <label>
                <input
                  type="radio"
                  name="resource-permission"
                  checked={app.permission() === permission}
                  onChange={() => app.setPermission(permission)}
                />
                {permission}
              </label>
            )}
          </For>
        </fieldset>
        <button
          onClick={() =>
            document
              .querySelectorAll<HTMLButtonElement>(
                ".resources-panel button[aria-expanded='true']",
              )
              .forEach((button) => button.click())
          }
        >
          Collapse All
        </button>
        <a class="mobile-access" href="#selected-resource">
          View Access ↓
        </a>
        <label class="page-size-control">
          Page Size{" "}
          <select
            aria-label="Page size"
            value={app.pageSize()}
            onChange={(e) =>
              app.setPageSize(
                Number(e.currentTarget.value) as import("../types").PageSize,
              )
            }
          >
            <For each={[5, 10, 20, 25, 50, 100, 250, 500, 1000]}>
              {(size) => <option>{size}</option>}
            </For>
          </select>
        </label>
      </div>
      <For
        each={app.bootstrapData()?.data.schema.resourceTypes ?? []}
        fallback={<EmptyState>No queryable resource types.</EmptyState>}
      >
        {(resourceType) => <ResourceTypeGroup resourceType={resourceType} />}
      </For>
    </div>
  );
}
